/**
 * Mobile Menu Navigation
 * Handles step-by-step navigation through 3 levels
 * - Open/close menu
 * - Navigate between levels
 * - Back button functionality
 */

import Animations from '../animations';

interface MobileMenuState {
  isOpen: boolean;
  currentLevelStack: string[];
}

class MobileMenu {
  private state: MobileMenuState = {
    isOpen: false,
    currentLevelStack: ['1'],
  };

  private menuElement: HTMLElement | null = null;
  private openBtn: HTMLElement | null = null;
  private closeBtn: HTMLElement | null = null;
  private backBtns: NodeListOf<HTMLElement> = document.querySelectorAll(
    '[data-back]'
  );
  private nextBtns: NodeListOf<HTMLElement> = document.querySelectorAll(
    '[data-target]'
  );
  private levels: NodeListOf<HTMLElement> = document.querySelectorAll(
    '[data-level]'
  );

  static init() {
    const instance = new MobileMenu();
    instance.bindEvents();
  }

  private bindEvents(): void {
    // Get elements
    this.menuElement = document.querySelector('[data-mobile-menu]');
    this.openBtn = document.querySelector('[data-mobile-open]');
    this.closeBtn = document.querySelector('[data-mobile-close]');

    const header = document.querySelector('[data-header]') as HTMLElement;
    if (header) {
      // const isHomePage = document.body.classList.contains('home');
      // const isSearchPage = document.body.classList.contains('search-result');
      const isTransparentHeaderWhite = document.body.classList.contains('has-transparent-header-white');
      const isTransparentHeaderColored = document.body.classList.contains('has-transparent-header-colored');

      if (isTransparentHeaderColored || isTransparentHeaderWhite) {
        const handleScroll = (): void => {
          header.classList.toggle('scrolled', window.scrollY >= 50);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
      } else {
        header.classList.add('scrolled');
      }
    }

    // Open menu
    if (this.openBtn) {
      this.openBtn.addEventListener('click', () => this.openMenu());
    }

    // Close menu
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeMenu());
    }

    // Back buttons
    this.backBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.goBack());
    });

    // Next buttons
    this.nextBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetId = btn.getAttribute('data-target');
        if (targetId) {
          this.goToLevel(targetId);
        }
      });
    });

    // Close menu when clicking backdrop
    if (this.menuElement) {
      this.menuElement.addEventListener('click', (e) => {
        if (e.target === this.menuElement) {
          this.closeMenu();
        }
      });
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.state.isOpen) {
        this.closeMenu();
      }
    });

    function initScrolledClass(
    target: HTMLElement = document.documentElement,
    threshold: number = 100
    ): () => void {
    const handleScroll = (): void => {
        const scrollY = window.scrollY ?? window.pageYOffset;
        target.classList.toggle('scrolled', scrollY >= threshold);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Run once on init to handle pre-scrolled state
    handleScroll();

    // Returns cleanup function
    return () => window.removeEventListener('scroll', handleScroll);
    }
  }

  private openMenu(): void {
    this.state.isOpen = true;
    this.state.currentLevelStack = ['1'];

    if (this.menuElement) {
      this.menuElement.classList.add('is-open');
    }

    // Show level 1, hide others
    this.levels.forEach((level) => {
      const levelNum = level.getAttribute('data-level');
      if (levelNum === '1') {
        level.classList.add('is-active');
      } else {
        level.classList.remove('is-active');
      }
    });

    // Animate the active level when menu opens.
    this.animateCurrentLevel();

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  private closeMenu(): void {
    this.state.isOpen = false;

    if (this.menuElement) {
      this.menuElement.classList.remove('is-open');
    }

    // Re-enable body scroll
    document.body.style.overflow = '';
  }

  private goToLevel(targetId: string): void {
    this.state.currentLevelStack.push(targetId);

    // Hide all levels
    this.levels.forEach((level) => {
      level.classList.remove('is-active');
    });

    // Show target level
    const targetLevel = document.querySelector(
      `[id="${targetId}"]`
    ) as HTMLElement;
    if (targetLevel) {
      targetLevel.classList.add('is-active');
      this.animateCurrentLevel();
    }
  }

  private goBack(): void {
    if (this.state.currentLevelStack.length > 1) {
      this.state.currentLevelStack.pop();
    }

    // Hide all levels
    this.levels.forEach((level) => {
      level.classList.remove('is-active');
    });

    // Show current level
    const currentLevelId = this.state.currentLevelStack[
      this.state.currentLevelStack.length - 1
    ];

    if (currentLevelId === '1') {
      const level1 = document.querySelector('[data-level="1"]') as HTMLElement;
      if (level1) {
        level1.classList.add('is-active');
        this.animateCurrentLevel();
      }
    } else {
      const targetLevel = document.querySelector(
        `[id="${currentLevelId}"]`
      ) as HTMLElement;
      if (targetLevel) {
        targetLevel.classList.add('is-active');
        this.animateCurrentLevel();
      }
    }
  }

  private animateCurrentLevel(): void {
    if (!this.menuElement) {
      return;
    }

    const activeLevel = this.menuElement.querySelector(
      '.mobile-menu__level.is-active'
    ) as HTMLElement | null;

    if (activeLevel) {
      Animations.animateMobileMenuLevel(activeLevel);
    }
  }
}

export default MobileMenu;
